import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, setDoc, doc, updateDoc, deleteDoc, getDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { useAuth } from './AuthContext';
import { uploadFileAndPrepareData } from '../utils/fileUpload';
import { extractFileContent } from '../utils/textExtractor';

const DocumentContext = createContext();

export const useDocuments = () => {
    const context = useContext(DocumentContext);
    if (!context) {
        throw new Error('useDocuments must be used within a DocumentProvider');
    }
    return context;
};

export const DocumentProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!currentUser) {
            setDocuments([]);
            setLoading(false);
            return;
        }

        const q = query(
            collection(db, 'documents'),
            where('userId', '==', currentUser.uid),
            orderBy('createdAt', 'desc'),
            orderBy('name', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const docs = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setDocuments(docs);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching documents:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser]);

    const uploadDocument = useCallback(async (file, app = 'contly', type = 'contracts', extraData = {}) => {
        if (!currentUser) throw new Error("User must be logged in to upload documents");

        const path = `users/${currentUser.uid}/${Date.now()}_${file.name}`;

        try {
            // 1. Upload to Storage and get base64 for Gemini
            const fileData = await uploadFileAndPrepareData(file, path);

            // 2. Extract content for the editor (HTML for Word, Text for TXT/MD)
            let extractedContent = '';
            try {
                extractedContent = await extractFileContent(file);
            } catch (err) {
                console.error("Failed to extract content for editor:", err);
            }

            // 3. Save metadata and content to Firestore
            const docRef = await addDoc(collection(db, 'documents'), {
                userId: currentUser.uid,
                name: file.name,
                title: file.name, // Add explicitly
                filename: file.name, // Add explicitly
                url: fileData.url,
                storagePath: path, // Save storage path for deletion
                mimeType: fileData.mimeType,
                app: app,
                type: type, // 'contracts', 'templates', 'tax', etc.
                category: extraData.category || type, // Use provided category or fallback to type
                content: extractedContent, // Store the extracted content so editor can show it
                ...extraData,
                createdAt: serverTimestamp(),
                size: file.size,
                status: 'ready'
            });

            return {
                id: docRef.id,
                name: file.name,
                title: file.name,
                filename: file.name,
                type: type,
                app: app,
                ...fileData
            };
        } catch (error) {
            console.error("Error uploading document:", error);
            throw error;
        }
    }, [currentUser]);

    const updateDocument = useCallback(async (docId, data) => {
        if (!currentUser) throw new Error("User must be logged in to update documents");
        try {
            const docRef = doc(db, 'documents', docId);
            await updateDoc(docRef, {
                ...data,
                updatedAt: serverTimestamp()
            });
        } catch (error) {
            console.error("Error updating document:", error);
            throw error;
        }
    }, [currentUser]);

    const deleteDocument = useCallback(async (docId) => {
        if (!currentUser) throw new Error("User must be logged in to delete documents");
        try {
            const docRef = doc(db, 'documents', docId);

            // 1. Get document data to find storage path
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                const data = docSnap.data();

                // 2. Determine storage path
                let storagePath = data.storagePath;

                // Fallback: If no storagePath, try to extract from URL (for legacy docs)
                if (!storagePath && data.url) {
                    try {
                        // Extract path from Firebase Storage URL
                        // Format: https://firebasestorage.googleapis.com/v0/b/[bucket]/o/[path]?alt=media...
                        const decodedUrl = decodeURIComponent(data.url);
                        const startIndex = decodedUrl.indexOf('/o/') + 3;
                        const endIndex = decodedUrl.indexOf('?');
                        if (startIndex > 2 && endIndex > startIndex) {
                            storagePath = decodedUrl.substring(startIndex, endIndex);
                        }
                    } catch (err) {
                        console.warn("Could not extract storage path from URL:", err);
                    }
                }

                // 3. Delete from Storage if path found
                if (storagePath) {
                    try {
                        const storageRef = ref(storage, storagePath);
                        await deleteObject(storageRef);
                    } catch (err) {
                        console.warn("Error deleting file from storage (might act successfully if file missing):", err);
                    }
                }
            }

            // 4. Delete from Firestore
            await deleteDoc(docRef);
        } catch (error) {
            console.error("Error deleting document:", error);
            throw error;
        }
    }, [currentUser]);

    const value = {
        documents,
        loading,
        uploadDocument,
        updateDocument,
        deleteDocument
    };

    return (
        <DocumentContext.Provider value={value}>
            {children}
        </DocumentContext.Provider>
    );
};
